import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  SlidersHorizontal,
  Clock,
  Calendar,
  Percent,
  Loader2,
  Save,
  CheckCircle,
  RefreshCw,
  Gift,
  Wallet,
  AlertTriangle,
  Truck,
  CreditCard,
  FileText,
  ToggleLeft,
  Package,
  FlaskConical,
  Trash2,
  Mail,
  MapPin,
  Gem,
  Plus,
  X,
  Store,
  ListOrdered,
  Tag
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { getAppSettings, updateAppSettings, purgeTestData, getTestDataCount } from '@/lib/api';

// Use require to avoid babel-metadata-plugin cross-file traversal crash on large files
const { WorkflowConfig, getExchangeSteps, getVoucherSteps, getRMASteps } = require('./WorkflowConfig');

const SETTING_GROUPS = [
  {
    id: 'windows',
    title: 'Finestre Temporali Reso',
    icon: Calendar,
    color: 'blue',
    fields: [
      { key: 'days_window_delivered', label: 'Giorni da consegna (delivered)', unit: 'giorni', description: 'Tempo massimo per richiedere un reso dalla data di consegna' },
      { key: 'days_window_fulfillment', label: 'Giorni da evasione (fulfillment)', unit: 'giorni', description: 'Tempo massimo per richiedere un reso dalla data di evasione (fallback)' },
    ]
  },
  {
    id: 'logistics',
    title: 'Controllo Logistica',
    icon: Clock,
    color: 'amber',
    fields: [
      { key: 'days_logistics_delivered', label: 'Alert logistica da consegna', unit: 'giorni', description: 'Dopo questi giorni dalla consegna, la logistica riceve un alert rosso' },
      { key: 'days_logistics_fulfillment', label: 'Alert logistica da evasione', unit: 'giorni', description: 'Dopo questi giorni dall\'evasione, la logistica riceve un alert rosso' },
    ]
  },
  {
    id: 'payments',
    title: 'Scadenze Pagamento',
    icon: CreditCard,
    color: 'purple',
    fields: [
      { key: 'payment_deadline_available_hours', label: 'Prodotto disponibile', unit: 'ore', description: 'Tempo per pagare il draft order (prodotto in stock)' },
      { key: 'payment_deadline_unavailable_hours', label: 'Prodotto non disponibile', unit: 'ore', description: 'Tempo per pagare il draft order (prodotto pre-order)' },
    ]
  },
  {
    id: 'costs',
    title: 'Costo Gestione Cambio',
    icon: Truck,
    color: 'rose',
    fields: [
      { key: 'exchange_fee', label: 'Cambio online: costo di gestione', unit: '\u20ac', description: 'Costo per richieste di cambio con ritiro/spedizione', step: '0.50' },
      { key: 'store_reservation_fee', label: 'Cambio in negozio: costo riserva', unit: '\u20ac', description: 'Costo per riservare il prodotto in negozio', step: '0.50' },
      { key: 'store_reservation_days', label: 'Giorni riserva prodotto', unit: 'giorni', description: 'Durata della riserva in negozio', step: '1' },
      { key: 'store_reservation_label', label: 'Etichetta costo riserva', unit: 'testo', description: 'Testo mostrato accanto al costo (es. "Riserva prodotto in negozio")', type: 'text' },
      { key: 'store_reservation_description', label: 'Descrizione riserva', unit: 'testo', description: 'Usa {days} per i giorni. Es: "Il prodotto sar\u00e0 riservato per {days} giorni..."', type: 'text' },
      { key: 'store_reservation_delivery_label', label: 'Descrizione opzione negozio', unit: 'testo', description: 'Testo sotto "Consegna in negozio" nella scelta metodo', type: 'text' },
    ]
  },
  {
    id: 'voucher',
    title: 'Voucher',
    icon: Percent,
    color: 'emerald',
    fields: [
      { key: 'voucher_bonus_percent', label: 'Bonus voucher', unit: '%', description: 'Percentuale di bonus aggiunta al valore del voucher' },
      { key: 'voucher_expiration_months', label: 'Scadenza voucher', unit: 'mesi', description: 'Durata di validita del voucher dalla data di emissione' },
      { key: 'rma_voucher_bonus_percent', label: 'Bonus voucher alternativo (RMA)', unit: '%', description: 'Bonus del voucher proposto come alternativa al rimborso nel flusso reso' },
    ]
  }
];

const COLOR_MAP = {
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'bg-blue-100 text-blue-600', header: 'text-blue-800' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'bg-amber-100 text-amber-600', header: 'text-amber-800' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'bg-purple-100 text-purple-600', header: 'text-purple-800' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'bg-emerald-100 text-emerald-600', header: 'text-emerald-800' },
  rose: { bg: 'bg-rose-50', border: 'border-rose-200', icon: 'bg-rose-100 text-rose-600', header: 'text-rose-800' },
};

const AdminControlPanelPage = () => {
  const [settings, setSettings] = useState(null);
  const [editedSettings, setEditedSettings] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [testDataCount, setTestDataCount] = useState(0);
  const [isPurging, setIsPurging] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await getAppSettings();
      setSettings(data);
      setEditedSettings(data);
      if (data.test_mode) loadTestCount();
    } catch (error) {
      toast.error('Errore nel caricamento delle impostazioni');
    }
    setIsLoading(false);
  };

  const loadTestCount = async () => {
    try {
      const data = await getTestDataCount();
      setTestDataCount(data.total);
    } catch { /* ignore */ }
  };

  const handleToggleTestMode = async (checked) => {
    setEditedSettings(prev => ({ ...prev, test_mode: checked }));
    setHasChanges(true);
    if (checked) loadTestCount();
  };

  const handlePurgeTestData = async () => {
    setIsPurging(true);
    try {
      const result = await purgeTestData();
      toast.success(result.message);
      setTestDataCount(0);
    } catch (error) {
      toast.error('Errore nell\'eliminazione dei dati test');
    }
    setIsPurging(false);
  };

  const handleChange = (key, value) => {
    const cleaned = String(value).replace(',', '.');
    if (cleaned === '' || cleaned === '.') return;
    const numValue = parseFloat(cleaned);
    if (isNaN(numValue) || numValue < 0) return;
    
    setEditedSettings(prev => ({ ...prev, [key]: numValue }));
    setHasChanges(true);
  };

  const handleToggle = (key, checked) => {
    setEditedSettings(prev => ({ ...prev, [key]: checked }));
    setHasChanges(true);
  };

  const handleSelect = (key, value) => {
    setEditedSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleNestedChange = (parentKey, childKey, value) => {
    setEditedSettings(prev => ({
      ...prev,
      [parentKey]: { ...(prev[parentKey] || {}), [childKey]: value }
    }));
    setHasChanges(true);
  };

  const handleNestedToggle = (parentKey, childKey, checked) => {
    setEditedSettings(prev => ({
      ...prev,
      [parentKey]: { ...(prev[parentKey] || {}), [childKey]: checked }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updated = await updateAppSettings(editedSettings);
      setSettings(updated);
      setEditedSettings(updated);
      setHasChanges(false);
      toast.success('Impostazioni salvate');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
    setIsSaving(false);
  };

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8" data-testid="control-panel-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-gray-900 flex items-center gap-3">
            <SlidersHorizontal className="w-7 h-7 text-gray-400" />
            Pannello di Controllo
          </h1>
          <p className="text-gray-500 text-sm mt-1">Configura tempistiche, scadenze e parametri del sistema</p>
        </div>
        
        <Button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className="flex items-center gap-2"
          data-testid="save-settings-btn"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : hasChanges ? (
            <Save className="w-4 h-4" />
          ) : (
            <CheckCircle className="w-4 h-4" />
          )}
          {isSaving ? 'Salvataggio...' : hasChanges ? 'Salva Modifiche' : 'Salvato'}
        </Button>
      </div>

      {/* Test Mode Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn(
          "mt-6 rounded-xl border overflow-hidden",
          editedSettings.test_mode
            ? "border-amber-400 bg-amber-50"
            : "border-gray-200 bg-white"
        )}
        data-testid="test-mode-card"
      >
        <div className={cn(
          "px-6 py-4 border-b flex items-center justify-between",
          editedSettings.test_mode
            ? "border-amber-300 bg-amber-100"
            : "border-gray-200 bg-gray-50"
        )}>
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-9 h-9 rounded-lg flex items-center justify-center",
              editedSettings.test_mode
                ? "bg-amber-500 text-white"
                : "bg-gray-200 text-gray-500"
            )}>
              <FlaskConical className="w-5 h-5" />
            </div>
            <div>
              <h2 className={cn(
                "font-semibold",
                editedSettings.test_mode ? "text-amber-800" : "text-gray-700"
              )}>
                Test Mode
              </h2>
              <p className="text-xs text-gray-500">
                {editedSettings.test_mode
                  ? 'Attiva — Le email NON vengono inviate. Le richieste sono marcate come test.'
                  : 'Disattivata — Il sistema funziona normalmente.'}
              </p>
            </div>
          </div>
          <Switch
            checked={!!editedSettings.test_mode}
            onCheckedChange={handleToggleTestMode}
            data-testid="test-mode-toggle"
          />
        </div>

        {editedSettings.test_mode && (
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="text-sm text-amber-700">
              <span className="font-medium">{testDataCount}</span> richieste di test nel sistema
            </div>
            <Button
              onClick={handlePurgeTestData}
              disabled={isPurging || testDataCount === 0}
              variant="outline"
              size="sm"
              className="border-red-300 text-red-600 hover:bg-red-50"
              data-testid="purge-test-data-btn"
            >
              {isPurging ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Pulisci dati test
            </Button>
          </div>
        )}
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {SETTING_GROUPS.map((group, idx) => {
          const Icon = group.icon;
          const colors = COLOR_MAP[group.color];
          
          return (
            <motion.div
              key={group.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className={`bg-white rounded-xl border border-gray-200 overflow-hidden`}
              data-testid={`settings-group-${group.id}`}
            >
              <div className={`px-6 py-4 border-b ${colors.border} ${colors.bg}`}>
                <h2 className={`font-semibold flex items-center gap-2 ${colors.header}`}>
                  <div className={`w-8 h-8 rounded-lg ${colors.icon} flex items-center justify-center`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  {group.title}
                </h2>
              </div>
              
              <div className="p-6 space-y-5">
                {group.fields.map(field => (
                  <div key={field.key}>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      {field.label}
                    </label>
                    <p className="text-xs text-gray-400 mb-2">{field.description}</p>
                    <div className="flex items-center gap-2">
                      <Input
                        type={field.type === 'text' ? 'text' : 'number'}
                        min={field.type === 'text' ? undefined : '0'}
                        step={field.type === 'text' ? undefined : (field.step || '1')}
                        value={editedSettings[field.key] ?? ''}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        className={field.type === 'text' ? 'flex-1' : 'w-32'}
                        data-testid={`setting-${field.key}`}
                      />
                      {field.type !== 'text' && (
                        <span className="text-sm text-gray-500">{field.unit}</span>
                      )}
                      {settings && editedSettings[field.key] !== settings[field.key] && (
                        <span className="text-xs text-amber-600 ml-2">
                          (era: {settings[field.key]} {field.unit})
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Fasce di Costo Reso — Dinamiche */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
        className="mt-6 bg-white rounded-xl border border-rose-200 overflow-hidden"
        data-testid="settings-group-cost-tiers"
      >
        <div className="px-6 py-4 border-b border-rose-200 bg-rose-50 flex items-center justify-between">
          <div>
            <h2 className="font-semibold flex items-center gap-2 text-rose-800">
              <div className="w-8 h-8 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center">
                <Truck className="w-4 h-4" />
              </div>
              Fasce di Costo Reso
            </h2>
            <p className="text-xs text-gray-500 mt-1 ml-11">Aggiungi, rimuovi e modifica le fasce di prezzo. Il costo dipende dalla fascia in cui ricade il prezzo del prodotto.</p>
          </div>
          <span className="text-xs font-medium text-rose-600 bg-rose-100 px-2.5 py-1 rounded-full">
            {(editedSettings.shipping_cost_tiers || []).length} {(editedSettings.shipping_cost_tiers || []).length === 1 ? 'fascia' : 'fasce'}
          </span>
        </div>
        <div className="p-6">
          {(() => {
            const tiers = editedSettings.shipping_cost_tiers || [];
            
            const updateTier = (idx, field, value) => {
              const cleaned = String(value).replace(',', '.');
              const num = cleaned === '' ? '' : Number(cleaned);
              const newTiers = tiers.map((t, i) => i === idx ? { ...t, [field]: num } : t);
              setEditedSettings(prev => ({ ...prev, shipping_cost_tiers: newTiers }));
              setHasChanges(true);
            };
            
            const addTier = () => {
              if (tiers.length >= 20) return;
              const lastWithLimit = [...tiers].filter(t => t.up_to !== null).sort((a, b) => (b.up_to || 0) - (a.up_to || 0))[0];
              const newThreshold = lastWithLimit ? (lastWithLimit.up_to || 0) + 100 : 100;
              const infinityIdx = tiers.findIndex(t => t.up_to === null);
              const newTier = { up_to: newThreshold, cost_first: 0, cost_additional: 0 };
              const newTiers = [...tiers];
              if (infinityIdx >= 0) {
                newTiers.splice(infinityIdx, 0, newTier);
              } else {
                newTiers.push(newTier);
              }
              setEditedSettings(prev => ({ ...prev, shipping_cost_tiers: newTiers }));
              setHasChanges(true);
            };
            
            const removeTier = (idx) => {
              if (tiers.length <= 1) return;
              const newTiers = tiers.filter((_, i) => i !== idx);
              setEditedSettings(prev => ({ ...prev, shipping_cost_tiers: newTiers }));
              setHasChanges(true);
            };
            
            // Sort for display: limited tiers ascending, then null (infinity)
            const sortedForDisplay = [...tiers].map((t, originalIdx) => ({ ...t, _idx: originalIdx }))
              .sort((a, b) => {
                const aVal = a.up_to === null ? Infinity : (a.up_to || 0);
                const bVal = b.up_to === null ? Infinity : (b.up_to || 0);
                return aVal - bVal;
              });

            return (
              <div className="space-y-0">
                {/* Table header */}
                <div className="grid grid-cols-[1fr_100px_100px_40px] gap-3 px-3 pb-2">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Fascia di prezzo</span>
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider text-center">1° Prodotto</span>
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider text-center">Aggiuntivo</span>
                  <span></span>
                </div>

                {/* Tier rows */}
                <div className="space-y-2">
                  {sortedForDisplay.map((tier, displayIdx) => {
                    const prevLimit = displayIdx > 0 ? sortedForDisplay[displayIdx - 1].up_to : 0;
                    const isInfinity = tier.up_to === null;
                    const rangeLabel = isInfinity
                      ? `€${prevLimit ?? 0}+`
                      : `€${prevLimit ?? 0} — €${tier.up_to ?? ''}`;
                    
                    const tierColors = [
                      'bg-blue-50 border-blue-200',
                      'bg-amber-50 border-amber-200',
                      'bg-rose-50 border-rose-200',
                      'bg-violet-50 border-violet-200',
                      'bg-emerald-50 border-emerald-200',
                      'bg-cyan-50 border-cyan-200',
                      'bg-orange-50 border-orange-200',
                      'bg-pink-50 border-pink-200',
                    ];
                    const tierTextColors = [
                      'text-blue-700',
                      'text-amber-700',
                      'text-rose-700',
                      'text-violet-700',
                      'text-emerald-700',
                      'text-cyan-700',
                      'text-orange-700',
                      'text-pink-700',
                    ];
                    const colorIdx = displayIdx % tierColors.length;

                    return (
                      <div key={tier._idx} className={`grid grid-cols-[1fr_100px_100px_40px] gap-3 items-center rounded-lg border px-3 py-3 ${tierColors[colorIdx]}`}>
                        <div className="flex items-center gap-2">
                          {!isInfinity ? (
                            <div className="flex items-center gap-1.5">
                              <span className={`text-xs font-medium ${tierTextColors[colorIdx]}`}>fino a</span>
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number" min="0" step="1"
                                  value={tier.up_to ?? ''}
                                  onChange={(e) => updateTier(tier._idx, 'up_to', e.target.value)}
                                  className="w-20 text-center text-sm font-semibold bg-white"
                                  data-testid={`tier-threshold-${tier._idx}`}
                                />
                                <span className="text-xs text-gray-400">€</span>
                              </div>
                            </div>
                          ) : (
                            <span className={`text-xs font-semibold ${tierTextColors[colorIdx]}`}>
                              da €{prevLimit ?? 0} in su
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-center gap-1">
                          <Input
                            type="number" min="0" step="0.5"
                            value={tier.cost_first ?? ''}
                            onChange={(e) => updateTier(tier._idx, 'cost_first', e.target.value)}
                            className="w-16 text-center text-sm font-medium bg-white"
                            data-testid={`tier-first-${tier._idx}`}
                          />
                          <span className="text-[10px] text-gray-400">€</span>
                        </div>
                        <div className="flex items-center justify-center gap-1">
                          <Input
                            type="number" min="0" step="0.5"
                            value={tier.cost_additional ?? ''}
                            onChange={(e) => updateTier(tier._idx, 'cost_additional', e.target.value)}
                            className="w-16 text-center text-sm font-medium bg-white"
                            data-testid={`tier-additional-${tier._idx}`}
                          />
                          <span className="text-[10px] text-gray-400">€</span>
                        </div>
                        <div className="flex justify-center">
                          {tiers.length > 1 && (
                            <button
                              onClick={() => removeTier(tier._idx)}
                              className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                              title="Rimuovi fascia"
                              data-testid={`tier-remove-${tier._idx}`}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Add tier button */}
                {tiers.length < 20 && (
                  <button
                    onClick={addTier}
                    className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 border-dashed border-gray-200 text-gray-400 hover:border-rose-300 hover:text-rose-500 hover:bg-rose-50/50 transition-all text-sm font-medium"
                    data-testid="add-tier-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Aggiungi fascia
                  </button>
                )}
              </div>
            );
          })()}
        </div>
      </motion.div>

      {/* Toggle Lettera di Vettura */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mt-6 bg-white rounded-xl border border-indigo-200 overflow-hidden"
        data-testid="settings-group-waybill"
      >
        <div className="px-6 py-4 border-b border-indigo-200 bg-indigo-50">
          <h2 className="font-semibold flex items-center gap-2 text-indigo-800">
            <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center">
              <FileText className="w-4 h-4" />
            </div>
            Lettera di Vettura
          </h2>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700 block">
                Abilita caricamento Lettera di Vettura
              </label>
              <p className="text-xs text-gray-400 mt-1">
                Quando attivo, permette di caricare un PDF della lettera di vettura nel dettaglio di ogni richiesta e inviarlo via email al cliente.
              </p>
            </div>
            <Switch
              checked={!!editedSettings.waybill_feature_enabled}
              onCheckedChange={(checked) => handleToggle('waybill_feature_enabled', checked)}
              data-testid="setting-waybill_feature_enabled"
            />
          </div>
          {editedSettings.waybill_feature_enabled && (
            <div className="mt-4 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
              <p className="text-xs text-indigo-700">
                La funzione e attiva. Nella pagina di dettaglio di ogni richiesta (esclusi i voucher) comparira il pulsante per caricare la lettera di vettura.
              </p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Servizi Attivi */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55 }}
        className="mt-6 bg-white rounded-xl border border-teal-200 overflow-hidden"
        data-testid="settings-group-services"
      >
        <div className="px-6 py-4 border-b border-teal-200 bg-teal-50">
          <h2 className="font-semibold flex items-center gap-2 text-teal-800">
            <div className="w-8 h-8 rounded-lg bg-teal-100 text-teal-600 flex items-center justify-center">
              <ToggleLeft className="w-4 h-4" />
            </div>
            Servizi Attivi per il Cliente
          </h2>
        </div>
        <div className="p-6 space-y-5">
          <p className="text-xs text-gray-500">Scegli quali opzioni di reso mostrare ai clienti nella pagina di scelta.</p>
          
          {[
            { key: 'service_exchange_enabled', label: 'Cambio', desc: 'Il cliente puo richiedere il cambio con un altro prodotto.', icon: RefreshCw },
            { key: 'service_voucher_enabled', label: 'Voucher', desc: 'Il cliente puo richiedere un buono acquisto (con bonus).', icon: Gift },
            { key: 'service_rma_enabled', label: 'Reso Monetario', desc: 'Il cliente puo richiedere il rimborso monetario (link sottolineato).', icon: Wallet },
          ].map(svc => (
            <div key={svc.key} className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <svc.icon className="w-5 h-5 text-gray-400" />
                <div>
                  <label className="text-sm font-medium text-gray-700 block">{svc.label}</label>
                  <p className="text-xs text-gray-400 mt-0.5">{svc.desc}</p>
                </div>
              </div>
              <Switch
                checked={!!editedSettings[svc.key]}
                onCheckedChange={(checked) => handleToggle(svc.key, checked)}
                data-testid={`setting-${svc.key}`}
              />
            </div>
          ))}

          {!editedSettings.service_exchange_enabled && !editedSettings.service_voucher_enabled && !editedSettings.service_rma_enabled && (
            <div className="p-3 bg-red-50 rounded-lg border border-red-100">
              <p className="text-xs text-red-700 font-medium">Attenzione: nessun servizio attivo. I clienti non potranno effettuare richieste.</p>
            </div>
          )}

          {/* RMA Button Style Toggle */}
          {editedSettings.service_rma_enabled && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <label className="text-xs font-medium text-gray-500 mb-2 block">Stile pulsante Reso</label>
              <div className="flex gap-2">
                {[
                  { value: 'minimal', label: 'Minimo', desc: 'Link sottolineato sotto le opzioni' },
                  { value: 'full', label: 'Massimo', desc: 'Card grande come Cambio e Voucher' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => handleToggle('rma_button_style', opt.value)}
                    className={cn(
                      "flex-1 p-3 rounded-lg border text-left transition-all",
                      (editedSettings.rma_button_style || 'minimal') === opt.value
                        ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                        : "border-gray-200 hover:border-gray-300"
                    )}
                    data-testid={`rma-style-${opt.value}`}
                  >
                    <p className="text-sm font-medium text-gray-800">{opt.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Consegna in Negozio */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.52 }}
        className="mt-6 bg-white rounded-xl border border-teal-200 overflow-hidden"
        data-testid="settings-group-store-delivery"
      >
        <div className="px-6 py-4 border-b border-teal-200 bg-teal-50">
          <h2 className="font-semibold flex items-center gap-2 text-teal-800">
            <div className="w-8 h-8 rounded-lg bg-teal-100 text-teal-600 flex items-center justify-center">
              <Store className="w-4 h-4" />
            </div>
            Consegna in Negozio
          </h2>
          <p className="text-xs text-gray-500 mt-1 ml-11">Permetti ai clienti di portare il pacco direttamente in negozio (costo €0). Non disponibile per il reso monetario.</p>
        </div>
        <div className="p-6 space-y-5">
          <div className="space-y-3">
            <div className="flex items-center justify-between py-1">
              <div>
                <label className="text-sm font-medium text-gray-700">Cambio — consegna in negozio</label>
                <p className="text-xs text-gray-400">I clienti che richiedono un cambio potranno scegliere di portare il pacco in negozio</p>
              </div>
              <Switch
                checked={!!editedSettings.store_delivery_exchange_enabled}
                onCheckedChange={(checked) => handleToggle('store_delivery_exchange_enabled', checked)}
                data-testid="setting-store_delivery_exchange_enabled"
              />
            </div>
            <div className="flex items-center justify-between py-1">
              <div>
                <label className="text-sm font-medium text-gray-700">Voucher — consegna in negozio</label>
                <p className="text-xs text-gray-400">I clienti che richiedono un voucher potranno scegliere di portare il pacco in negozio</p>
              </div>
              <Switch
                checked={!!editedSettings.store_delivery_voucher_enabled}
                onCheckedChange={(checked) => handleToggle('store_delivery_voucher_enabled', checked)}
                data-testid="setting-store_delivery_voucher_enabled"
              />
            </div>
          </div>

          <div className="border-t border-teal-100 pt-4">
            <p className="text-xs font-semibold text-gray-600 mb-3">Tempistiche consegna in negozio</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Giorni massimi dal ricevimento pacco</label>
                <p className="text-[10px] text-gray-400 mb-1.5">Il cliente ha questo numero di giorni per portare il pacco in negozio</p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number" min="1" step="1"
                    value={editedSettings.store_delivery_max_days ?? ''}
                    onChange={(e) => handleChange('store_delivery_max_days', e.target.value)}
                    className="w-20 text-center"
                    data-testid="setting-store_delivery_max_days"
                  />
                  <span className="text-sm text-gray-500">giorni</span>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Giorni dopo la notifica (casi eccezionali)</label>
                <p className="text-[10px] text-gray-400 mb-1.5">Se la notifica arriva tardi, il cliente ha almeno questi giorni</p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number" min="1" step="1"
                    value={editedSettings.store_delivery_notification_days ?? ''}
                    onChange={(e) => handleChange('store_delivery_notification_days', e.target.value)}
                    className="w-20 text-center"
                    data-testid="setting-store_delivery_notification_days"
                  />
                  <span className="text-sm text-gray-500">giorni</span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-3 bg-teal-50 rounded-lg border border-teal-100">
            <p className="text-xs text-teal-700">
              Il cliente riceverà un avviso nella pagina di conferma e nell'email che dovrà attendere una comunicazione prima di recarsi in negozio. Lo staff verrà avvisato nello stesso momento.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Voucher Rimborso Cambio — Automatico/Manuale */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55 }}
        className="mt-6 bg-white rounded-xl border border-emerald-200 overflow-hidden"
        data-testid="settings-group-auto-refund-voucher"
      >
        <div className="px-6 py-4 border-b border-emerald-200 bg-emerald-50">
          <h2 className="font-semibold flex items-center gap-2 text-emerald-800">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center">
              <Gift className="w-4 h-4" />
            </div>
            Voucher Rimborso Cambio
          </h2>
          <p className="text-xs text-gray-500 mt-1 ml-11">Quando un cambio ha una differenza a favore del cliente, genera un voucher Shopify con l'importo da rimborsare.</p>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between py-1">
            <div>
              <label className="text-sm font-medium text-gray-700">Generazione automatica</label>
              <p className="text-xs text-gray-400">Se attivo, il voucher viene generato automaticamente quando l'ordine di cambio viene evaso su Shopify. Se disattivo, dovrai generarlo manualmente dal dettaglio richiesta.</p>
            </div>
            <Switch
              checked={!!editedSettings.auto_exchange_refund_voucher}
              onCheckedChange={(checked) => handleToggle('auto_exchange_refund_voucher', checked)}
              data-testid="setting-auto_exchange_refund_voucher"
            />
          </div>
          <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
            <p className="text-xs text-emerald-700">
              {editedSettings.auto_exchange_refund_voucher
                ? '✅ Automatico: il voucher verrà generato e inviato al cliente appena l\'ordine viene evaso su Shopify.'
                : '✋ Manuale: dovrai cliccare "Genera Voucher Rimborso" nel dettaglio della richiesta per creare il voucher.'}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Reso Post-Cambio */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.56 }}
        className="mt-6 bg-white rounded-xl border border-violet-200 overflow-hidden"
        data-testid="settings-group-post-exchange-return"
      >
        <div className="px-6 py-4 border-b border-violet-200 bg-violet-50">
          <h2 className="font-semibold flex items-center gap-2 text-violet-800">
            <div className="w-8 h-8 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center">
              <RefreshCw className="w-4 h-4" />
            </div>
            Reso Post-Cambio
          </h2>
          <p className="text-xs text-gray-500 mt-1 ml-11">Quando un cliente vuole fare il reso di un prodotto ricevuto dopo un cambio, il sistema calcola il rimborso dall'ordine originale.</p>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between py-1">
            <div>
              <label className="text-sm font-medium text-gray-700">Abilita reso post-cambio</label>
              <p className="text-xs text-gray-400">Se attivo, il sistema rileva automaticamente gli ordini di cambio e calcola il rimborso dall'ordine madre.</p>
            </div>
            <Switch
              checked={!!editedSettings.post_exchange_return_enabled}
              onCheckedChange={(checked) => handleToggle('post_exchange_return_enabled', checked)}
              data-testid="setting-post_exchange_return_enabled"
            />
          </div>
        </div>
      </motion.div>

      {/* Collezione Sposa — Costi e Flussi */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55 }}
        className="mt-6 bg-white rounded-xl border border-pink-200 overflow-hidden"
        data-testid="settings-group-bridal"
      >
        <div className="px-6 py-4 border-b border-pink-200 bg-pink-50">
          <h2 className="font-semibold flex items-center gap-2 text-pink-800">
            <div className="w-8 h-8 rounded-lg bg-pink-100 text-pink-600 flex items-center justify-center">
              <Gem className="w-4 h-4" />
            </div>
            Collezione Sposa
          </h2>
          <p className="text-xs text-gray-500 mt-1 ml-11">Costi e flussi dedicati agli abiti della collezione sposa</p>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Costo fisso reso sposa</label>
            <p className="text-xs text-gray-400 mb-2">Prezzo minimo indicato al cliente per il reso di un abito sposa (i tessuti pregiati possono costare di più)</p>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="0"
                step="1"
                value={editedSettings.bridal_return_cost ?? ''}
                onChange={(e) => handleChange('bridal_return_cost', e.target.value)}
                className="w-32"
                data-testid="setting-bridal_return_cost"
              />
              <span className="text-sm text-gray-500">&euro;</span>
            </div>
          </div>

          <div className="border-t border-pink-100 pt-4">
            <div className="flex items-center justify-between py-1">
              <div>
                <label className="text-sm font-medium text-gray-700">Richieste via WhatsApp</label>
                <p className="text-xs text-gray-400">Quando attivo, i clienti con abiti sposa vengono reindirizzati a WhatsApp per gestire la richiesta</p>
              </div>
              <Switch
                checked={!!editedSettings.bridal_whatsapp_enabled}
                onCheckedChange={(checked) => handleToggle('bridal_whatsapp_enabled', checked)}
                data-testid="setting-bridal_whatsapp_enabled"
              />
            </div>
          </div>

          <div className="p-3 bg-pink-50 rounded-lg border border-pink-100">
            <p className="text-xs text-pink-700">
              Gli abiti vengono riconosciuti automaticamente come "sposa" in base al titolo, tipo prodotto e tag Shopify.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Metodo di Spedizione per Flusso */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="mt-6 bg-white rounded-xl border border-sky-200 overflow-hidden"
        data-testid="settings-group-shipping-methods"
      >
        <div className="px-6 py-4 border-b border-sky-200 bg-sky-50">
          <h2 className="font-semibold flex items-center gap-2 text-sky-800">
            <div className="w-8 h-8 rounded-lg bg-sky-100 text-sky-600 flex items-center justify-center">
              <Package className="w-4 h-4" />
            </div>
            Metodo di Spedizione per Flusso
          </h2>
        </div>
        <div className="p-6 space-y-5">
          <p className="text-xs text-gray-500">Scegli come gestire la spedizione del reso per ciascun flusso.</p>
          
          {[
            { key: 'shipping_method_exchange', label: 'Cambio', icon: RefreshCw },
            { key: 'shipping_method_voucher', label: 'Voucher', icon: Gift },
            { key: 'shipping_method_rma', label: 'Reso Monetario', icon: Wallet },
          ].map(flow => (
            <div key={flow.key} className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <flow.icon className="w-5 h-5 text-gray-400" />
                <label className="text-sm font-medium text-gray-700">{flow.label}</label>
              </div>
              <Select
                value={editedSettings[flow.key] || 'pickup'}
                onValueChange={(val) => handleSelect(flow.key, val)}
              >
                <SelectTrigger className="w-64" data-testid={`setting-${flow.key}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pickup">Ritiro da parte nostra (corriere)</SelectItem>
                  <SelectItem value="label">Spedizione cliente libera</SelectItem>
                  <SelectItem value="free">Spedizione cliente (etichetta nostra)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ))}

          <div className="p-3 bg-sky-50 rounded-lg border border-sky-100 text-xs text-sky-700 space-y-1">
            <p><strong>Ritiro (corriere):</strong> Il corriere passa a ritirare il pacco. Admin carica la lettera di vettura.</p>
            <p><strong>Libera (cliente):</strong> Il cliente sceglie il corriere. Nessun documento da caricare.</p>
            <p><strong>Etichetta nostra:</strong> Il cliente porta il pacco in posta. Admin carica l'etichetta di spedizione.</p>
          </div>

          {/* Testi visibili al cliente per ogni metodo */}
          <div className="border-t border-sky-100 pt-5 mt-5">
            <p className="text-xs font-semibold text-gray-600 mb-4 uppercase tracking-wider">Testi visibili al cliente</p>
            {[
              { method: 'pickup', color: 'blue' },
              { method: 'label', color: 'amber' },
              { method: 'free', color: 'emerald' },
            ].map(({ method, color }) => {
              const labels = editedSettings.delivery_method_labels || {};
              const labelKey = `${method}_label`;
              const descKey = `${method}_description`;
              return (
                <div key={method} className={`mb-4 p-3 bg-${color}-50 rounded-lg border border-${color}-100`}>
                  <p className="text-xs font-bold text-gray-600 mb-2 uppercase">{method === 'pickup' ? 'Ritiro' : method === 'label' ? 'Autonoma' : 'Guidata'}</p>
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-gray-500 block mb-0.5">Titolo</label>
                      <input
                        type="text"
                        className="w-full border border-gray-200 rounded-md px-3 py-1.5 text-sm"
                        value={labels[labelKey] || ''}
                        onChange={(e) => {
                          const updated = { ...editedSettings.delivery_method_labels, [labelKey]: e.target.value };
                          setEditedSettings(prev => ({ ...prev, delivery_method_labels: updated }));
                          setHasChanges(true);
                        }}
                        data-testid={`delivery-label-${method}`}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-0.5">Descrizione</label>
                      <input
                        type="text"
                        className="w-full border border-gray-200 rounded-md px-3 py-1.5 text-sm"
                        value={labels[descKey] || ''}
                        onChange={(e) => {
                          const updated = { ...editedSettings.delivery_method_labels, [descKey]: e.target.value };
                          setEditedSettings(prev => ({ ...prev, delivery_method_labels: updated }));
                          setHasChanges(true);
                        }}
                        data-testid={`delivery-desc-${method}`}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Etichette Spedizione su Shopify */}
          <div className="border-t border-sky-100 pt-5 mt-5">
            <p className="text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Etichette Spedizione su Shopify</p>
            <p className="text-xs text-gray-400 mb-4">Testo che appare come metodo di spedizione negli ordini di cambio creati su Shopify.</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 block mb-0.5">Spedizione standard (ritiro / autonoma)</label>
                <input
                  type="text"
                  className="w-full border border-gray-200 rounded-md px-3 py-1.5 text-sm"
                  value={editedSettings.shopify_shipping_label || ''}
                  onChange={(e) => {
                    setEditedSettings(prev => ({ ...prev, shopify_shipping_label: e.target.value }));
                    setHasChanges(true);
                  }}
                  placeholder="Spedizione autonoma del cliente"
                  data-testid="cp-shopify-shipping-label"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-0.5">Consegna in negozio</label>
                <input
                  type="text"
                  className="w-full border border-gray-200 rounded-md px-3 py-1.5 text-sm"
                  value={editedSettings.shopify_shipping_label_store || ''}
                  onChange={(e) => {
                    setEditedSettings(prev => ({ ...prev, shopify_shipping_label_store: e.target.value }));
                    setHasChanges(true);
                  }}
                  placeholder="Consegna in negozio"
                  data-testid="cp-shopify-shipping-label-store"
                />
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Configurazione Flussi Dinamica */}

      {/* Passaggi "Come funziona" per il cliente */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.72 }}
        className="mt-6 bg-white rounded-xl border border-violet-200 overflow-hidden"
        data-testid="settings-group-come-funziona"
      >
        <div className="px-6 py-4 border-b border-violet-200 bg-violet-50">
          <h2 className="font-semibold flex items-center gap-2 text-violet-800">
            <div className="w-8 h-8 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center">
              <ListOrdered className="w-4 h-4" />
            </div>
            Passaggi "Come funziona" (visibili al cliente)
          </h2>
          <p className="text-xs text-gray-500 mt-1 ml-11">Modifica i passaggi mostrati nella pagina di riepilogo. Lascia vuoto un passaggio per nasconderlo.</p>
        </div>
        <div className="p-6 space-y-6">
          {[
            { type: 'exchange', typeLabel: 'Cambio', methods: [
              { key: 'pickup', label: 'Ritiro a domicilio' },
              { key: 'label', label: 'Spedizione autonoma' },
              { key: 'free', label: 'Spedizione guidata' }
            ]},
            { type: 'voucher', typeLabel: 'Voucher', methods: [
              { key: 'pickup', label: 'Ritiro a domicilio' },
              { key: 'label', label: 'Spedizione autonoma' },
              { key: 'free', label: 'Spedizione guidata' }
            ]},
            { type: 'rma', typeLabel: 'Reso Monetario', methods: [
              { key: 'pickup', label: 'Ritiro a domicilio' },
              { key: 'label', label: 'Spedizione autonoma' },
              { key: 'free', label: 'Spedizione guidata' }
            ]}
          ].map(({ type, typeLabel, methods }) => {
            const activeMethod = editedSettings[`shipping_method_${type}`] || 'pickup';
            const stepsKey = `${type}_${activeMethod}`;
            const allSteps = editedSettings.come_funziona_steps || {};
            const steps = allSteps[stepsKey] || [];

            return (
              <div key={type} className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold text-gray-700">{typeLabel}</p>
                  <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full font-medium">
                    Metodo attivo: {methods.find(m => m.key === activeMethod)?.label}
                  </span>
                </div>
                
                {/* Tabs for shipping methods */}
                <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                  {methods.map(m => (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => {
                        // Just switch the visible tab — we store a local UI state
                        const el = document.querySelector(`[data-tab="${type}_${m.key}"]`);
                        const siblings = document.querySelectorAll(`[data-tab-group="${type}"]`);
                        siblings.forEach(s => s.classList.add('hidden'));
                        el?.classList.remove('hidden');
                        // Update active tab buttons
                        const btns = document.querySelectorAll(`[data-tab-btn-group="${type}"]`);
                        btns.forEach(b => { b.classList.remove('bg-white', 'shadow-sm', 'text-violet-700'); b.classList.add('text-gray-500'); });
                        const activeBtn = document.querySelector(`[data-tab-btn="${type}_${m.key}"]`);
                        activeBtn?.classList.add('bg-white', 'shadow-sm', 'text-violet-700');
                        activeBtn?.classList.remove('text-gray-500');
                      }}
                      className={cn(
                        "flex-1 text-xs py-1.5 px-2 rounded-md font-medium transition-all",
                        m.key === activeMethod ? "bg-white shadow-sm text-violet-700" : "text-gray-500"
                      )}
                      data-tab-btn={`${type}_${m.key}`}
                      data-tab-btn-group={type}
                    >
                      {m.label}
                      {m.key === activeMethod && <span className="ml-1 text-[10px]">(attivo)</span>}
                    </button>
                  ))}
                </div>

                {/* Step editors per method */}
                {methods.map(m => {
                  const mStepsKey = `${type}_${m.key}`;
                  const mSteps = allSteps[mStepsKey] || [];
                  return (
                    <div
                      key={m.key}
                      data-tab={`${type}_${m.key}`}
                      data-tab-group={type}
                      className={m.key !== activeMethod ? 'hidden' : ''}
                    >
                      <div className="space-y-2">
                        {[0, 1, 2, 3].map(idx => {
                          const step = mSteps[idx] || { title: '', desc: '' };
                          return (
                            <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                              <div className="w-7 h-7 bg-violet-100 text-violet-600 rounded flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                                {idx + 1}
                              </div>
                              <div className="flex-1 space-y-1.5">
                                <input
                                  type="text"
                                  placeholder={`Titolo passaggio ${idx + 1} (vuoto = nascosto)`}
                                  className="w-full border border-gray-200 rounded-md px-3 py-1.5 text-sm font-medium"
                                  value={step.title || ''}
                                  onChange={(e) => {
                                    const newSteps = [...(allSteps[mStepsKey] || [])];
                                    while (newSteps.length <= idx) newSteps.push({ title: '', desc: '' });
                                    newSteps[idx] = { ...newSteps[idx], title: e.target.value };
                                    setEditedSettings(prev => ({
                                      ...prev,
                                      come_funziona_steps: { ...prev.come_funziona_steps, [mStepsKey]: newSteps }
                                    }));
                                    setHasChanges(true);
                                  }}
                                  data-testid={`step-title-${mStepsKey}-${idx}`}
                                />
                                <input
                                  type="text"
                                  placeholder="Descrizione (opzionale)"
                                  className="w-full border border-gray-200 rounded-md px-3 py-1.5 text-xs text-gray-500"
                                  value={step.desc || ''}
                                  onChange={(e) => {
                                    const newSteps = [...(allSteps[mStepsKey] || [])];
                                    while (newSteps.length <= idx) newSteps.push({ title: '', desc: '' });
                                    newSteps[idx] = { ...newSteps[idx], desc: e.target.value };
                                    setEditedSettings(prev => ({
                                      ...prev,
                                      come_funziona_steps: { ...prev.come_funziona_steps, [mStepsKey]: newSteps }
                                    }));
                                    setHasChanges(true);
                                  }}
                                  data-testid={`step-desc-${mStepsKey}-${idx}`}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Indirizzo Spedizione Reso */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.75 }}
        className="mt-6 bg-white rounded-xl border border-orange-200 overflow-hidden"
        data-testid="settings-group-return-address"
      >
        <div className="px-6 py-4 border-b border-orange-200 bg-orange-50">
          <h2 className="font-semibold flex items-center gap-2 text-orange-800">
            <div className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center">
              <MapPin className="w-4 h-4" />
            </div>
            Indirizzo Spedizione Reso
          </h2>
          <p className="text-xs text-gray-500 mt-1 ml-11">Indirizzo mostrato nelle email come destinazione per la spedizione del reso</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: 'name', label: 'Nome / Ragione Sociale', placeholder: 'DEA FEA' },
              { key: 'street', label: 'Via e Numero Civico', placeholder: 'Via Biagio Riccio, 65' },
              { key: 'city', label: 'Città', placeholder: 'Giugliano in Campania' },
              { key: 'zip', label: 'CAP', placeholder: '80014' },
              { key: 'province', label: 'Provincia', placeholder: 'NA' },
              { key: 'country', label: 'Nazione', placeholder: 'Italia' },
            ].map(f => (
              <div key={f.key}>
                <label className="text-xs font-medium text-gray-500 block mb-1">{f.label}</label>
                <Input
                  value={(editedSettings.return_shipping_address || {})[f.key] || ''}
                  onChange={(e) => handleNestedChange('return_shipping_address', f.key, e.target.value)}
                  placeholder={f.placeholder}
                  className="text-sm"
                  data-testid={`return-address-${f.key}`}
                />
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Configurazione Flussi Dinamica */}
      <div className="mt-10" data-testid="flows-config">
        <h2 className="text-xl font-heading font-semibold text-gray-900 mb-2">Configurazione Flussi</h2>
        <p className="text-sm text-gray-500 mb-6">Attiva o disattiva gli step per ciascun flusso. Gli step grigi sono obbligatori.</p>

        <div className="space-y-6">
          {/* Toggle prodotti non disponibili nel cambio - PRIMA del flusso */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl border border-blue-200 overflow-hidden"
            data-testid="exchange-unavailable-toggle"
          >
            <div className="px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
                  <RefreshCw className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">Cambio con prodotti non disponibili</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {editedSettings.exchange_unavailable_enabled
                      ? 'I clienti possono selezionare prodotti non disponibili (richiede approvazione admin)'
                      : 'I clienti possono cambiare solo con prodotti attualmente disponibili'
                    }
                  </p>
                </div>
              </div>
              <Switch
                checked={!!editedSettings.exchange_unavailable_enabled}
                onCheckedChange={(checked) => {
                  setEditedSettings(prev => ({ ...prev, exchange_unavailable_enabled: checked }));
                  setHasChanges(true);
                }}
                data-testid="exchange-unavailable-toggle-btn"
              />
            </div>
          </motion.div>

          {/* CAMBIO */}
          <WorkflowConfig
            flowKey="exchange"
            title="Flusso Cambio"
            icon={RefreshCw}
            color="blue"
            config={editedSettings.workflow_exchange || { initial_approval: false, final_approval: false }}
            onToggle={(stepKey, val) => {
              const wf = { ...(editedSettings.workflow_exchange || {}), [stepKey]: val };
              setEditedSettings(prev => ({ ...prev, workflow_exchange: wf }));
              setHasChanges(true);
            }}
            steps={getExchangeSteps(editedSettings.workflow_exchange || {})}
          />

          {/* VOUCHER */}
          <WorkflowConfig
            flowKey="voucher"
            title="Flusso Voucher"
            icon={Gift}
            color="emerald"
            config={editedSettings.workflow_voucher || { initial_approval: false, final_approval: true }}
            onToggle={(stepKey, val) => {
              const wf = { ...(editedSettings.workflow_voucher || {}), [stepKey]: val };
              setEditedSettings(prev => ({ ...prev, workflow_voucher: wf }));
              setHasChanges(true);
            }}
            steps={getVoucherSteps(editedSettings.workflow_voucher || {})}
          />

          {/* RESO MONETARIO */}
          <WorkflowConfig
            flowKey="rma"
            title="Flusso Reso Monetario"
            icon={Wallet}
            color="amber"
            config={editedSettings.workflow_rma || { initial_approval: true, final_approval: true }}
            onToggle={(stepKey, val) => {
              const wf = { ...(editedSettings.workflow_rma || {}), [stepKey]: val };
              setEditedSettings(prev => ({ ...prev, workflow_rma: wf }));
              setHasChanges(true);
            }}
            steps={getRMASteps(editedSettings.workflow_rma || {})}
          />

          {/* Alert Logistica */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-white rounded-xl border border-red-200 overflow-hidden"
          >
            <div className="px-6 py-4 bg-red-50 border-b border-red-200">
              <h3 className="font-semibold text-red-800 flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-red-100 text-red-600 flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4" />
                </div>
                Controllo Tempistiche Logistica
              </h3>
            </div>
            <div className="p-6 text-sm text-gray-700 space-y-2">
              <p>Se il pacco del reso arriva oltre <strong>{editedSettings.days_logistics_delivered || 19} giorni</strong> dalla consegna (delivered) o oltre <strong>{editedSettings.days_logistics_fulfillment || 22} giorni</strong> dall'evasione (fulfillment), la logistica riceve un <span className="text-red-600 font-semibold">alert rosso</span>.</p>
              <p>In questo caso il pacco <strong>non deve essere accettato</strong> dal corriere perché il cliente non ha rispettato le tempistiche contrattuali.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AdminControlPanelPage;
